"use client";

import { Checkbox, CheckboxIndicator } from "@radix-ui/react-checkbox";
import throttle from "lodash.throttle";
import { CheckIcon, PlusCircle } from "lucide-react";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import * as React from "react";

import { Spinner } from "@/components/icons/spinner";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@/components/ui/sheet";
import { fetchWrapper } from "@/lib/hooks/fetch-client";
import { useMemberSettings } from "@/lib/hooks/use-settings";
import { cn } from "@/lib/utils";
import { filterRFCategories } from "@/lib/utils/research-feed";
import { Category, Response, Settings, User } from "@/types";

import { toast } from "./ui/use-toast";

interface CustomizeSummariesProps extends React.HTMLAttributes<HTMLDivElement> {
	user: User;
	settings: Settings;
}

type PresetValue = "fitness" | "aging" | "diet" | "all";
type Preset = {
	title: string;
	value: PresetValue;
};

const presets: Preset[] = [
	{ title: "Fitness", value: "fitness" },
	{ title: "Aging", value: "aging" },
	{ title: "Diets & Supplements", value: "diet" },
	{ title: "All", value: "all" },
];

const CustomizeSummaries = ({ children, className, user, ...props }: CustomizeSummariesProps) => {
	const router = useRouter();
	const searchParams = useSearchParams();
	const dialogRef = React.useRef<HTMLDivElement>(null);
	const closeRef = React.useRef(null);
	const [scrolled, setScrolled] = React.useState(false);
	const [loading, setLoading] = React.useState(false);
	const [saving, setSaving] = React.useState(false);
	const [open, setOpen] = React.useState(false);

	const [categories, setCategories] = React.useState<Category[]>([]);
	const [selectedCategories, setSelectedCategories] = React.useState<string[]>([]);
	const [summaryMultiCat, setSummaryMuliCat] = React.useState("0");
	const [summaryNotification, setSummaryNotification] = React.useState("week");
	const { settings } = useMemberSettings({ condition: open });

	const frequencies = [
		{
			name: "Daily",
			value: "daily",
		},
		{
			name: "Weekly",
			value: "week",
		},
		{
			name: "Monthly",
			value: "month",
		},
		{
			name: "No Emails",
			value: "none",
		},
	];

	const multicat_options = [
		{
			name: "Yes",
			value: "1",
		},
		{
			name: "No",
			value: "0",
		},
	];

	React.useEffect(() => {
		if (!user) return;
		setSummaryMuliCat(user?.summary_multicat.toString() || "0");
		setSummaryNotification(user?.summary_notification || "week");
	}, [user]);

	React.useEffect(() => {
		const dialog = dialogRef.current;
		if (!dialog && !open) return;

		const handleScroll = throttle(() => {
			const { scrollTop, scrollHeight, clientHeight } = dialog;
			const $scrolled = scrollTop + clientHeight === scrollHeight;

			if (scrolled !== $scrolled) {
				setScrolled($scrolled);
			}
		}, 200);

		dialog?.addEventListener("scroll", handleScroll);
		return () => {
			dialog?.removeEventListener("scroll", handleScroll);
		};
	}, [open, scrolled]);

	React.useEffect(() => {
		if (!open || categories?.length) return;

		(async () => {
			setLoading(true);
			try {
				const categories: Response<Category[]> = await fetchWrapper(
					`/v1/study-summaries/categories`
				);

				if (categories?.data) {
					const filteredCategories = filterRFCategories(categories.data);
					setCategories(filteredCategories);
				}
			} catch (err) {
				// console.error(err);
			} finally {
				setLoading(false);
			}
		})();
	}, [open, categories]);

	React.useEffect(() => {
		if (!open || !settings?.categories.length) return;

		setSelectedCategories(settings?.categories.map((cat) => cat.id));
	}, [open, settings]);

	React.useEffect(() => {
		if (searchParams.get("customize") != "1") return;
		const params = new URLSearchParams(searchParams.toString());
		params.delete("customize");

		window.history.pushState(window.history.state, "", `?${params.toString()}`);
		setOpen(true);
	}, [open, searchParams]);

	const selectCategory = (id: string) => {
		const isSelected = selectedCategories.includes(id);
		setSelectedCategories(
			isSelected ? selectedCategories.filter((cat) => cat !== id) : [...selectedCategories, id]
		);
	};

	const handlePreset = (name: PresetValue) => {
		const setPreset = (ids: string | string[]) => {
			setSelectedCategories(
				categories
					.filter((cat) => {
						return ids.includes(cat.id);
					})
					.map((cat) => cat.id)
			);
		};

		switch (name) {
			case "fitness":
				setPreset([
					"4OQx0Qg", // Paint joints bones
					"njlBbl5", // muscle gain
					"3d2oqJg", // fat lost
				]);
				break;
			case "aging":
				setPreset([
					"6D2LrJj", // immunity
					"4OQx0Qg", // Paint joints bones
					"oRQbm2Y", // Diabetes
					"eV28P2Y", // Cancer
					"4ZQGAlY", // Healthy aging
					"4ZJ73JV", // Cardiosvacular
				]);
				break;
			case "diet":
				setPreset([
					"MGlXBQ8", // Veggetarian
					"e4QdO2b", // Vitamins and minerals
					"5zlRyJL", // Herbal supps
					"djlaoQ1", // Diets and fodos
					"WVlkX2Y", // Low-carb
				]);
				break;
			case "all":
				setSelectedCategories(categories.map((cat) => cat.id));
				break;
		}
	};

	const handleSave = async () => {
		const payload = {
			categories: ["jb2wblB", ...selectedCategories],
			summary_multicat: summaryMultiCat,
			summary_notification: summaryNotification,
		};

		const path = `/research-feed/?${searchParams.toString()}`;
		const previewSecret = process.env.previewSecret;

		setSaving(true);
		try {
			await fetchWrapper(`/v1/members/configure`, {
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(payload),
			});
			router.push(`/api/revalidate?secret=${previewSecret}&path=${encodeURIComponent(path)}`);
		} catch (err) {
			toast({
				variant: "error",
				title: "Error",
				description: "Failed to save settings",
			});
		} finally {
			setSaving(false);
			closeRef.current.click();
		}
	};

	const handleOpenChange = (e) => {
		if (e && !user) {
			router.push("/signup/?source=research_feed");
			return;
		}
		setOpen(e);
	};

	return (
		<Sheet open={open} onOpenChange={handleOpenChange}>
			<SheetTrigger className="w-full" asChild>
				{children}
			</SheetTrigger>
			<SheetContent
				side={"right"}
				className="overflow-x-hidden pt-0"
				onClick={(e) => e.stopPropagation()}
				ref={dialogRef}
			>
				<SheetHeader className="sticky top-0 mx-auto flex flex-row items-center justify-between space-y-0 bg-white pt-6">
					<SheetTitle>Customize</SheetTitle>
					<SheetClose ref={closeRef} className="flex rotate-45 items-center">
						<PlusCircle />
					</SheetClose>
				</SheetHeader>
				{loading ? (
					<div className="flex h-full items-center justify-center">
						<Spinner />
					</div>
				) : (
					<section className={cn("w-full space-y-6 pt-4")} {...props}>
						<div className="mx-auto w-full max-w-md">
							<h3 className="text-lg font-bold leading-8 -tracking-2 text-examine-purple-500">
								Show the same summary in multiple categories?
							</h3>
							<p className="text-md text-gray-600">A summary may fit into multiple categories:</p>
							<RadioGroup
								defaultValue={summaryMultiCat}
								onValueChange={(value) => setSummaryMuliCat(value)}
								className="space-2 mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4"
							>
								{multicat_options.map((option, index) => (
									<RadioGroupItem
										key={option.value + index}
										value={option.value}
										id={option.value}
										label={option.name}
										variant="checkbox-style"
									/>
								))}
							</RadioGroup>
						</div>

						<div className="mx-auto w-full max-w-md">
							<h3 className="text-lg font-bold leading-8 -tracking-2 text-examine-purple-500">
								How frequently do you want an email with the latest study summaries:
							</h3>
							<RadioGroup
								defaultValue={summaryNotification}
								onValueChange={(value) => setSummaryNotification(value)}
								className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-3 lg:gap-3"
							>
								{frequencies.map((frequency, index) => (
									<RadioGroupItem
										key={frequency.value + index}
										value={frequency.value}
										id={frequency.value}
										label={frequency.name}
										variant="checkbox-style"
									/>
								))}
							</RadioGroup>
						</div>

						<div className="mx-auto w-full max-w-md">
							<h3 className="text-lg font-bold leading-8 -tracking-2 text-examine-purple-500">
								Categories Selection
							</h3>
							<p className="text-md text-gray-600">Presets:</p>

							<div className="mb-6 mt-2 flex flex-wrap items-center gap-2">
								{presets.map((preset) => (
									<Button
										type="button"
										variant="outline"
										size="sm"
										className="border-examine-purple-100 text-primary transition-colors duration-200 hover:bg-primary hover:text-white"
										key={preset.value}
										onClick={() => handlePreset(preset.value)}
									>
										{preset.title}
									</Button>
								))}
							</div>
							<div>
								<div className="mb-10 space-y-5">
									{categories.map(
										(cat, index) =>
											cat.name !== "Editor's Picks" && (
												<Checkbox
													id={cat.id}
													key={cat.id}
													className="flex w-full  items-center justify-between rounded-lg border border-gray-200 px-5 py-3 transition-shadow duration-200 hover:shadow"
													value={cat.id}
													data-name={cat.name}
													checked={selectedCategories.includes(cat.id)}
													onCheckedChange={() => selectCategory(cat.id)}
												>
													<label
														key={index}
														htmlFor={cat.id}
														className="flex cursor-pointer items-center"
													>
														<Image
															width={25}
															height={25}
															src={cat.icon}
															alt={`${cat.name} image`}
														/>
														<span className="ml-2.5 sm:text-lg">{cat.name}</span>
													</label>
													<div className="size-5 rounded-md border-2 border-examine-green-500">
														<CheckboxIndicator asChild>
															<span className="flex size-full items-center justify-center bg-examine-green-500">
																<CheckIcon className="size-3 stroke-white" strokeWidth="4" />
															</span>
														</CheckboxIndicator>
													</div>
												</Checkbox>
											)
									)}
								</div>
							</div>
						</div>

						<div className="sticky -bottom-6 mt-15 flex w-full items-center justify-between bg-white p-4">
							<Button
								type="button"
								variant="text"
								onClick={() => setSelectedCategories([])}
								className={cn(
									!scrolled && "opacity-0",
									"text-lg font-medium leading-8 -tracking-2 text-gray-600  hover:text-primary"
								)}
							>
								Deselect All
							</Button>

							<Button type="submit" className="px-4 py-2" onClick={() => handleSave()}>
								{saving && <Spinner color="text-white" size="5" />}
								Save Settings
							</Button>
						</div>
					</section>
				)}
			</SheetContent>
		</Sheet>
	);
};

export { CustomizeSummaries };
